
import { useState, useEffect } from 'react';

interface FilterSectionProps {
  filters: {
    specialty: string;
    location: string;
    minPrice: string;
    maxPrice: string;
    sortBy: string;
  };
  onFilterChange: (filters: any) => void;
}

export default function FilterSection({ filters, onFilterChange }: FilterSectionProps) {
  const [selectedSpecialty, setSelectedSpecialty] = useState(filters.specialty || '');
  const [priceRange, setPriceRange] = useState('');
  const [location, setLocation] = useState(filters.location || '');
  const [sortBy, setSortBy] = useState(filters.sortBy || '');

  // Mettre à jour les états locaux quand les filtres externes changent
  useEffect(() => {
    setLocation(filters.location || '');
    setSelectedSpecialty(filters.specialty || '');
    setSortBy(filters.sortBy || '');
  }, [filters]);

  const specialties = [
    'Tous les domaines',
    'Musculation & Fitness',
    'Yoga & Pilates',
    'Nutrition & Bien-être',
    'Cardio Training',
    'HIIT & CrossFit',
    'Sport Prénatal/Postnatal',
    'Coaching Senior',
    'Préparation Sportive',
    'Coaching Duo/Groupe'
  ];

  const priceRanges = [
    { label: 'Tous les prix', min: '', max: '' },
    { label: 'Moins de 50€', min: '', max: '50' },
    { label: '50€ - 70€', min: '50', max: '70' },
    { label: '70€ - 100€', min: '70', max: '100' },
    { label: '100€ - 150€', min: '100', max: '150' },
    { label: 'Plus de 150€', min: '150', max: '' }
  ];

  const sortOptions = [
    { label: 'Pertinence', value: 'rating' },
    { label: 'Prix croissant', value: 'price_asc' },
    { label: 'Prix décroissant', value: 'price_desc' },
    { label: 'Expérience', value: 'experience' },
    { label: 'Nombre d\'avis', value: 'reviews' }
  ];

  const handleSpecialtyChange = (value: string) => {
    setSelectedSpecialty(value);
    onFilterChange({
      specialty: value,
      location,
      minPrice: filters.minPrice,
      maxPrice: filters.maxPrice,
      sortBy
    });
  };

  const handlePriceChange = (value: string) => {
    setPriceRange(value);
    const selectedRange = priceRanges.find(r => r.label === value);
    onFilterChange({
      specialty: selectedSpecialty,
      location,
      minPrice: selectedRange?.min || '',
      maxPrice: selectedRange?.max || '',
      sortBy
    });
  };

  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocation(value);
    onFilterChange({
      specialty: selectedSpecialty,
      location: value,
      minPrice: filters.minPrice,
      maxPrice: filters.maxPrice,
      sortBy
    });
  };

  const handleSortChange = (value: string) => {
    setSortBy(value);
    onFilterChange({
      specialty: selectedSpecialty,
      location,
      minPrice: filters.minPrice,
      maxPrice: filters.maxPrice,
      sortBy: value
    });
  };

  const clearFilters = () => {
    setSelectedSpecialty('');
    setPriceRange('');
    setLocation('');
    setSortBy('');
    onFilterChange({
      specialty: '',
      location: '',
      minPrice: '',
      maxPrice: '',
      sortBy: 'rating'
    });
  };

  return (
    <section className="py-8 bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
          {/* Filters */}
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
              {/* Location Filter - Input */}
              <div className="relative min-w-64">
                <input
                  type="text"
                  value={location}
                  onChange={handleLocationChange}
                  placeholder="Ville ou code postal..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-map-pin-line text-gray-400"></i>
                </div>
              </div>

              {/* Specialty Filter */}
              <div className="relative min-w-48">
                <select 
                  value={selectedSpecialty}
                  onChange={(e) => handleSpecialtyChange(e.target.value)}
                  className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer text-sm"
                >
                  {specialties.map((specialty) => (
                    <option key={specialty} value={specialty === 'Tous les domaines' ? '' : specialty}>
                      {specialty}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <i className="ri-arrow-down-s-line text-gray-400"></i>
                </div>
              </div>
              
              {/* Price Filter */}
              <div className="relative min-w-40">
                <select 
                  value={priceRange}
                  onChange={(e) => handlePriceChange(e.target.value)}
                  className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer text-sm"
                >
                  {priceRanges.map((range) => (
                    <option key={range.label} value={range.label}>
                      {range.label}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <i className="ri-arrow-down-s-line text-gray-400"></i>
                </div>
              </div>
            </div>
            
            {/* Sort and Clear */}
            <div className="flex gap-4 w-full lg:w-auto">
              <div className="relative min-w-48">
                <select 
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                  className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer text-sm"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <i className="ri-sort-desc text-gray-400 mr-1"></i>
                  <i className="ri-arrow-down-s-line text-gray-400"></i>
                </div>
              </div>

              <button
                onClick={clearFilters}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-refresh-line mr-2"></i>
                Effacer
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
